import * as React from 'react';

import { noteSessionRefusal } from '../auth/signOutReason';
import config from '../config';
import { ContextStore } from '../store/contextStore';
import type { BackgroundTaskRow, SyncCatalogue, SyncCatalogueLoad, SyncOptions, SyncTask } from './types';

// The Booking-system sync page's calls (contract: apiv2/dashboard_sync.openapi.yaml, keyed by
// operationId so syncContract.test.js can hold every path and method to the contract).
//
// Plain fetch, not the data provider, as the Onboarding page does (onboarding/api.ts): the provider
// signs the session out on any 403, while here a 403 means "not your permission" (`permission_denied`,
// `bgtask_permission_required`) -- an answer the page shows. It also keeps the franchise wrapper from
// adding `franchise_to` / `tour_operator` to these own-tenant calls. A 401 still signs out.
export const ENDPOINTS = {
    dashboardSyncOptions: { method: 'GET', path: '/v2/edit/sync/' },
    dashboardSyncCatalogue: { method: 'GET', path: '/v2/edit/sync/products/catalogue/' },
    dashboardSyncLoadCatalogue: { method: 'POST', path: '/v2/edit/sync/products/catalogue/' },
    dashboardSyncProducts: { method: 'POST', path: '/v2/edit/sync/products/' },
    dashboardSyncAux: { method: 'POST', path: '/v2/edit/sync/aux/' },
    bgTaskDetail: { method: 'GET', path: '/v2/edit/bg_tasks/{task_id}/' },
    bgTaskApprove: { method: 'POST', path: '/v2/edit/bg_tasks/{task_id}/approve/' },
    bgTaskCancel: { method: 'POST', path: '/v2/edit/bg_tasks/{task_id}/cancel/' },
} as const;

export type OperationId = keyof typeof ENDPOINTS;

const EDIT_PREFIX = '/v2/edit';

/** A refused or failed call. `code` is the contract's stable code when the body has one;
 *  `fieldErrors` are DRF's `{<field>: [<message>, …]}` of a 400 the endpoint could not read. */
export class SyncApiError extends Error {
    status: number;
    code: string | null;
    body: any;
    fieldErrors: Record<string, string[]>;
    retryAfter: number | null;

    constructor(status: number, body: any, retryAfter: number | null = null) {
        super(errorMessage(status, body));
        this.name = 'SyncApiError';
        this.status = status;
        this.body = body;
        this.code = (body && typeof body.code === 'string' && body.code) || null;
        this.fieldErrors = fieldErrorsOf(body);
        this.retryAfter = retryAfter;
    }
}

const fieldErrorsOf = (body: any): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        return out;
    }
    Object.entries(body).forEach(([field, value]) => {
        if (Array.isArray(value)) {
            out[field] = value.map(String);
        }
    });
    return out;
};

function errorMessage(status: number, body: any): string {
    // `detail` on the sync endpoints, `message` on the bg-task API.
    const words = body && (typeof body.detail === 'string' ? body.detail : typeof body.message === 'string' ? body.message : '');
    if (words) {
        return words;
    }
    const fields = Object.values(fieldErrorsOf(body)).flat();
    if (fields.length > 0) {
        return fields.join(' ');
    }
    if (status === 0) {
        return 'The server could not be reached. Check your connection and try again.';
    }
    if (status === 403) {
        return 'You do not have permission to do this.';
    }
    if (status === 404) {
        return 'Not found.';
    }
    if (status >= 500) {
        return 'The server could not answer. Try again in a moment.';
    }
    return `The request was refused (${status}).`;
}

export const errorCode = (e: unknown): string | null => (e instanceof SyncApiError ? e.code : null);

/** A `/v2/edit/...` path with its `{params}` filled and its query (empty values left out), on the API origin. */
export function editUrl(
    endpointPath: string,
    pathParams: Record<string, string | number> = {},
    query: Record<string, string | null | undefined> = {},
    apiUrl = config.apiUrl,
) {
    let path = endpointPath;
    Object.entries(pathParams).forEach(([name, value]) => {
        path = path.replace(`{${name}}`, encodeURIComponent(String(value)));
    });
    const search = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
        if (v != null && v !== '') {
            search.set(k, v);
        }
    });
    const qs = search.toString();
    return `${apiUrl}${path.slice(EDIT_PREFIX.length)}${qs ? `?${qs}` : ''}`;
}

export function buildUrl(
    operation: OperationId,
    pathParams: Record<string, string | number> = {},
    query: Record<string, string | null | undefined> = {},
    apiUrl = config.apiUrl,
) {
    return editUrl(ENDPOINTS[operation].path, pathParams, query, apiUrl);
}

export interface ClientOptions {
    getToken: () => string | null | undefined;
    onUnauthorized: () => void;
    apiUrl?: string;
}

interface CallOptions {
    path?: Record<string, string | number>;
    query?: Record<string, string | null | undefined>;
    json?: any;
    signal?: AbortSignal;
}

/** `account` as the contract takes it: left out for the default account. */
const accountOf = (account: string | null | undefined) => (account ? { account } : {});

type EndpointMap = Record<string, { method: string; path: string }>;

/** The call of a client keyed by operation (this page's, the booking sync form's on the bg-task API): the token per
 *  call, a 401 signs out (noting a refused session's reason), any other refusal is a `SyncApiError` the page shows. */
export function createCaller<E extends EndpointMap>(endpoints: E, { getToken, onUnauthorized, apiUrl = config.apiUrl }: ClientOptions) {
    return async <T,>(operation: keyof E, { path = {}, query = {}, json, signal }: CallOptions = {}): Promise<T> => {
        const { method } = endpoints[operation];
        const headers: Record<string, string> = { Accept: 'application/json', Authorization: `Token ${getToken()}` };
        if (json !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        let res: Response;
        try {
            res = await fetch(editUrl(endpoints[operation].path, path, query, apiUrl), {
                method,
                headers,
                cache: 'no-cache',
                body: json !== undefined ? JSON.stringify(json) : undefined,
                signal,
            });
        } catch (e: any) {
            if (e && e.name === 'AbortError') {
                throw e;
            }
            throw new SyncApiError(0, null);
        }
        let data: any = null;
        const ctype = res.headers.get('Content-Type') || '';
        if (res.status !== 204 && ctype.includes('json')) {
            try {
                data = await res.json();
            } catch (_e) {
                data = null;
            }
        }
        if (res.status === 401) {
            // A session the backend no longer accepts (two-step sign-in now required, or SSO only): the sign-in
            // page says why after the sign-out, as for the data provider's 401s (auth/signOutReason.js).
            noteSessionRefusal(data);
            onUnauthorized();
            throw new SyncApiError(401, data);
        }
        if (res.status < 200 || res.status >= 300) {
            const retry = Number(res.headers.get('Retry-After'));
            throw new SyncApiError(res.status, data, Number.isFinite(retry) && retry > 0 ? retry : null);
        }
        return data as T;
    };
}

export function createSyncApi(options: ClientOptions) {
    const call = createCaller(ENDPOINTS, options);

    return {
        options: (signal?: AbortSignal) => call<SyncOptions>('dashboardSyncOptions', { signal }),
        catalogue: (integration: string, account: string | null, signal?: AbortSignal) =>
            call<SyncCatalogue>('dashboardSyncCatalogue', { query: { integration, account }, signal }),
        loadCatalogue: (integration: string, account: string | null, refresh = false) =>
            call<SyncCatalogueLoad>('dashboardSyncLoadCatalogue', {
                json: { integration, ...accountOf(account), ...(refresh ? { refresh: true } : {}) },
            }),
        syncProducts: (integration: string, account: string | null, productIds: string[]) =>
            call<SyncTask>('dashboardSyncProducts', { json: { integration, ...accountOf(account), product_ids: productIds } }),
        syncAux: (integration: string, account: string | null) =>
            call<SyncTask>('dashboardSyncAux', { json: { integration, ...accountOf(account) } }),
        task: (taskId: string, signal?: AbortSignal) => call<BackgroundTaskRow>('bgTaskDetail', { path: { task_id: taskId }, signal }),
        approve: (taskId: string) => call<{ message: string }>('bgTaskApprove', { path: { task_id: taskId }, json: {} }),
        cancel: (taskId: string) => call<{ message: string }>('bgTaskCancel', { path: { task_id: taskId }, json: {} }),
    };
}

export type SyncApi = ReturnType<typeof createSyncApi>;

/** A client for the signed-in session (`create` is a module-level factory). Identity-stable: the token is read
 *  per call (it rotates), and a 401 signs out and comes back to the page the user was on. */
export function useSessionClient<A>(create: (options: ClientOptions) => A): A {
    const { token, logout } = React.useContext(ContextStore) as any;
    const tokenRef = React.useRef(token);
    tokenRef.current = token;
    const logoutRef = React.useRef(logout);
    logoutRef.current = logout;
    return React.useMemo(
        () => create({
            getToken: () => tokenRef.current,
            onUnauthorized: () => logoutRef.current?.(window.location.pathname),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
}

/** The sync API for the signed-in session. */
export const useSyncApi = (): SyncApi => useSessionClient(createSyncApi);
